import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Building2, Calendar, Hash, Save, X, Mail, Phone as PhoneIcon, Landmark } from 'lucide-react';

const Invoicingvsm = () => {
    const now = new Date();
    const formattedNow = now.toISOString().slice(0, 16);

    const [formData, setFormData] = useState({
        invoiceNumber: '',
        customer: '',
        email: '',
        phone: '',
        billingAddress: '',
        shippingAddress: '',
        gstin: '',
        date: formattedNow,
        items: [{ description: '', cutDimensions: '', qty: '', rate: '' }]
    });

    const [customers, setCustomers] = useState([]);
    const [clothingSheets, setClothingSheets] = useState([]);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch Leads and Clothing Sheets
    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = JSON.parse(sessionStorage.getItem("userData"));
                if (!user?.id) {
                    console.warn("User ID not found in sessionStorage");
                    return;
                }

                // 1. Fetch Leads
                const leadResponse = await axios.get(`https://cloudapi.emedhatechnologies.com/pages/api/lead_management`, {
                    params: { user_id: user.id }
                });
                const interestedLeads = leadResponse.data.filter(cust => cust.status === "INTERESTED");
                setCustomers(interestedLeads);

                // 2. Fetch Clothing Sheets / Cuts data
                const sheetsResponse = await axios.get(`https://cloudapi.emedhatechnologies.com/pages/api/clothingsheets`);
                setClothingSheets(sheetsResponse.data);

            } catch (err) {
                console.error('Error fetching data:', err);
            }
        };
        fetchData();
    }, []);

    // Handle normal input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle selecting customer from dropdown & mapping items based on customer ID
    const handleCustomerSelect = (e) => {
        const selectedId = e.target.value;
        const selectedCustomer = customers.find(c => c.id.toString() === selectedId);

        if (selectedCustomer) {
            let mappedItems = [];

            clothingSheets.forEach(sheet => {
                if (sheet.cuts && Array.isArray(sheet.cuts)) {
                    sheet.cuts.forEach(cut => {
                        // Match customerId (checking both customerId and customer_id safely)
                        const cutCustId = cut.customerId || cut.customer_id;
                        if (cutCustId && cutCustId.toString() === selectedId.toString()) {
                            mappedItems.push({
                                description: `${sheet.sheetName || 'Sheet'} (${sheet.category || 'General'}) - Note: ${cut.note || 'N/A'}`,
                                cutDimensions: cut.cutDimensions || '',
                                qty: cut.cutQty || 1,
                                rate: 0
                            });
                        }
                    });
                }
            });

            // If no matching items found via customer ID, fallback to 1 empty row
            if (mappedItems.length === 0) {
                mappedItems = [{ description: '', cutDimensions: '', qty: '', rate: '' }];
            }

            setFormData(prev => ({
                ...prev,
                customer: selectedCustomer.name || "",
                email: selectedCustomer.email || "",
                phone: selectedCustomer.contact || "",
                gstin: selectedCustomer.gst || "",
                billingAddress: selectedCustomer.location || "",
                shippingAddress: selectedCustomer.location || "",
                items: mappedItems
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                customer: '',
                email: '',
                phone: '',
                gstin: '',
                billingAddress: '',
                shippingAddress: '',
                items: [{ description: '', cutDimensions: '', qty: '', rate: '' }]
            }));
        }
    };

    // Handle item changes
    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = (field === 'qty' || field === 'rate') ? Number(value) : value;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    // Remove item rows
    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, items: newItems.length > 0 ? newItems : [{ description: '', cutDimensions: '', qty: '', rate: '' }] }));
    };

    // Calculations
    const calculateSubtotal = () => formData.items.reduce((acc, item) => acc + (Number(item.qty) || 0) * (Number(item.rate) || 0), 0);
    const calculateGST = () => calculateSubtotal() * 0.18;
    const calculateTotal = () => calculateSubtotal() + calculateGST();

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const user = JSON.parse(sessionStorage.getItem("userData"));
            if (!user?.id) {
                alert("User not logged in");
                return;
            }
            const payload = { ...formData, user_id: user.id };
            const response = await axios.post("https://cloudapi.emedhatechnologies.com/pages/api/api/invoicesvsm", payload);
            console.log(response.data);
            alert("Invoice saved successfully!");
            const now = new Date();
            const formattedNow = now.toISOString().slice(0, 16);
            setFormData({
                invoiceNumber: '',
                customer: '',
                email: '',
                phone: '',
                billingAddress: '',
                shippingAddress: '',
                gstin: '',
                date: formattedNow,
                items: [{ description: '', cutDimensions: '', qty: '', rate: '' }]
            });
        } catch (err) {
            console.error("Error saving invoice:", err);
            alert("Failed to save invoice.");
        }
    };

    // Responsive breakpoints
    const isDesktop = windowWidth >= 1024;
    const isTablet = windowWidth >= 768;

    // Shared UI Styling Variables
    const cardStyle = { backgroundColor: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' };
    const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' };
    const baseInputStyle = { width: '100%', padding: '10px 12px', fontSize: '14px', backgroundColor: '#f9f9f9', border: '1px solid #e5e5e5', borderRadius: '12px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s ease' };

    const renderStyledInput = (label, name, type = "text", value = "", onChange, iconElement = null) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>{label}</label>
            <div style={{ position: 'relative' }}>
                {iconElement && (
                    <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', color: '#a3a3a3', pointerEvents: 'none' }}>
                        {iconElement}
                    </div>
                )}
                <input name={name} type={type} value={value} onChange={onChange} style={{ ...baseInputStyle, paddingLeft: iconElement ? '36px' : '12px' }} />
            </div>
        </div>
    );

    const renderStyledTextarea = (label, name, value, onChange) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>{label}</label>
            <textarea name={name} value={value} onChange={onChange} rows={3} style={{ ...baseInputStyle, resize: 'none' }} />
        </div>
    );

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', backgroundColor: '#fafafa', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', boxSizing: 'border-box' }}>

            {/* Header Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: '1px solid #e5e5e5', paddingBottom: '16px', marginBottom: '24px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#171717', margin: 0 }}>Invoice Form</h1>
                <p style={{ color: '#737373', fontSize: '14px', margin: 0 }}>Create and balance operational transaction parameters</p>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'minmax(0, 2.8fr) minmax(0, 1.2fr)' : '1fr', gap: '24px', alignItems: 'start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <FileText style={{ width: '16px', height: '16px', color: '#2563eb' }} />
                                <h2 style={{ color: '#262626', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Invoice Details</h2>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : '1fr', gap: '16px' }}>
                                {renderStyledInput("Invoice Number", "invoiceNumber", "text", formData.invoiceNumber, handleInputChange, <Hash style={{ width: '16px', height: '16px' }} />)}
                                {renderStyledInput("Invoice Date", "date", "datetime-local", formData.date, handleInputChange, <Calendar style={{ width: '16px', height: '16px' }} />)}
                            </div>
                        </div>

                        {/* Section Block 2: Profile CRM Information */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Building2 style={{ width: '16px', height: '16px', color: '#a855f7' }} />
                                <h2 style={{ color: '#262626', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Customer Info</h2>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Select Customer</label>
                                    <select onChange={handleCustomerSelect} value={customers.find(c => c.name === formData.customer)?.id || ''} style={{ ...baseInputStyle, height: '42px' }}>
                                        <option value="">-- Select Customer --</option>
                                        {customers.map(cust => (
                                            <option key={cust.id} value={cust.id}>{cust.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isTablet ? 'repeat(3, 1fr)' : '1fr', gap: '16px' }}>
                                    {renderStyledInput("Email", "email", "email", formData.email, handleInputChange, <Mail style={{ width: '16px', height: '16px' }} />)}
                                    {renderStyledInput("Phone", "phone", "text", formData.phone, handleInputChange, <PhoneIcon style={{ width: '16px', height: '16px' }} />)}
                                    {renderStyledInput("GSTIN", "gstin", "text", formData.gstin, handleInputChange, <Landmark style={{ width: '16px', height: '16px' }} />)}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : '1fr', gap: '16px' }}>
                                    {renderStyledTextarea("Billing Address", "billingAddress", formData.billingAddress, handleInputChange)}
                                    {renderStyledTextarea("Shipping Address", "shippingAddress", formData.shippingAddress, handleInputChange)}
                                </div>
                            </div>
                        </div>

                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <h2 style={{ color: '#262626', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Line Items (Mapped from Customer Sheets)</h2>
                            </div>

                            {/* Responsive Table Headings for Large Monitors */}
                            {isTablet && (
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e5e5', marginBottom: '8px' }}>
                                    <span style={labelStyle}>Description</span>
                                    <span style={{ ...labelStyle, textAlign: 'center' }}>Cut Dimensions</span>
                                    <span style={{ ...labelStyle, textAlign: 'center' }}>Qty</span>
                                    <span style={{ ...labelStyle, textAlign: 'right' }}>Rate</span>
                                    <span style={{ ...labelStyle, textAlign: 'right' }}>Amount</span>
                                    <span style={{ width: '24px' }} />
                                </div>
                            )}

                            <div>
                                {formData.items.map((item, index) => (
                                    <div key={index} style={{ display: 'grid', gridTemplateColumns: isTablet ? '2fr 1fr 1fr 1fr auto' : '1fr', gap: '12px', padding: isTablet ? '8px 0' : '16px 0', borderBottom: isTablet ? 'none' : '1px dashed #e5e5e5', alignItems: isTablet ? 'end' : 'stretch' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {!isTablet && <span style={labelStyle}>Description</span>}
                                            <input value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Item dynamic details description..." style={{ ...baseInputStyle, backgroundColor: '#ffffff' }} />
                                        </div>

                                        {isTablet ? (
                                            <>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <input type="text" value={item.cutDimensions} placeholder="e.g. 200x300" onChange={e => handleItemChange(index, 'cutDimensions', e.target.value)} style={{ ...baseInputStyle, textAlign: 'center', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <input type="number" value={item.qty} placeholder="0" onChange={e => handleItemChange(index, 'qty', e.target.value)} style={{ ...baseInputStyle, textAlign: 'center', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <input type="number" value={item.rate} placeholder="0.00" onChange={e => handleItemChange(index, 'rate', e.target.value)} style={{ ...baseInputStyle, textAlign: 'right', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', height: '42px', justifyContent: 'center', textAlign: 'right', minWidth: '60px' }}>
                                                    <span style={{ fontWeight: '600', color: '#171717', fontSize: '14px' }}>
                                                        ₹{((item.qty || 0) * (item.rate || 0)).toFixed(2)}
                                                    </span>
                                                </div>
                                                <button type="button" onClick={() => removeItem(index)} style={{ backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fee2e2', padding: '10px', cursor: 'pointer', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px' }}>
                                                    <X style={{ width: '16px', height: '16px' }} />
                                                </button>
                                            </>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) auto', gap: '12px', alignItems: 'end' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={labelStyle}>Dimensions</span>
                                                    <input type="text" value={item.cutDimensions} placeholder="Dimensions" onChange={e => handleItemChange(index, 'cutDimensions', e.target.value)} style={{ ...baseInputStyle, textAlign: 'center', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={labelStyle}>Qty</span>
                                                    <input type="number" value={item.qty} placeholder="0" onChange={e => handleItemChange(index, 'qty', e.target.value)} style={{ ...baseInputStyle, textAlign: 'center', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={labelStyle}>Rate</span>
                                                    <input type="number" value={item.rate} placeholder="0.00" onChange={e => handleItemChange(index, 'rate', e.target.value)} style={{ ...baseInputStyle, textAlign: 'right', backgroundColor: '#ffffff' }} />
                                                </div>
                                                <button type="button" onClick={() => removeItem(index)} style={{ backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fee2e2', padding: '10px', cursor: 'pointer', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px' }}>
                                                    <X style={{ width: '16px', height: '16px' }} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Sticky Sidebar */}
                    <div style={{ position: isDesktop ? 'sticky' : 'static', top: isDesktop ? '24px' : 'auto' }}>
                        <div style={cardStyle}>
                            <h3 style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a3a3a3', borderBottom: '1px solid #f5f5f5', paddingBottom: '8px', margin: '0 0 16px 0' }}>Summary Metrics</h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                                    <span style={{ color: '#737373' }}>Subtotal:</span>
                                    <span style={{ fontWeight: '500', color: '#262626' }}>₹{calculateSubtotal().toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                                    <span style={{ color: '#737373' }}>GST (18%):</span>
                                    <span style={{ fontWeight: '500', color: '#262626' }}>₹{calculateGST().toFixed(2)}</span>
                                </div>

                                <div style={{ borderTop: '1px solid #f5f5f5', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#171717' }}>Total Amount:</span>
                                    <span style={{ fontSize: '20px', fontWeight: '700', color: '#2563eb' }}>₹{calculateTotal().toFixed(2)}</span>
                                </div>
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <button type="submit" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 0', backgroundColor: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.15)' }}>
                                    <Save style={{ width: '16px', height: '16px' }} />
                                    Save Invoice
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </form>
        </div>
    );
};

export default Invoicingvsm;